import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react"
import * as Y from "yjs"
import { Awareness, removeAwarenessStates } from "y-protocols/awareness"
import { useDaily, useDailyEvent, useMeetingState } from "@daily-co/daily-react"
import { DailyTransport } from "@/lib/live/daily-transport"
import { dailyAppMessageSend } from "@/lib/live/daily-send"
import { LiveYjsProvider } from "@/lib/live/yjs-provider"
import { graphqlClient } from "@/lib/graphql"
import { graphql } from "@/__generated__"
import { restoreSavedNotes } from "./notesDoc"

// Stable per-participant cursor colors, keyed off the Yjs clientID.
const CURSOR_COLORS = [
  "#ef4444", // red
  "#3b82f6", // blue
  "#22c55e", // green
  "#f97316", // orange
  "#a855f7", // purple
  "#ec4899", // pink
  "#14b8a6", // teal
  "#eab308", // yellow
]

function colorForClient(clientId: number): string {
  return CURSOR_COLORS[clientId % CURSOR_COLORS.length]
}

export interface LiveDocValue {
  // The shared document. When collaboration is disabled (flag off) this is a
  // local-only Y.Doc with no transport attached — consumers use it the same way,
  // they just don't see a peer. That keeps a single code path either way.
  doc: Y.Doc
  awareness: Awareness
  // Whether the real-time layer is on (flag enabled) for this viewer.
  enabled: boolean
  // True once we've completed the initial state exchange with a peer.
  synced: boolean
  // This client's stable cursor color.
  selfColor: string
  // The lesson this classroom is for (absent in the marketing-demo fallback).
  lessonId?: string
  // The solo practice classroom (#945): local-only doc, nothing persisted, and
  // the flashcard-capture surface renders as a disabled placeholder instead of
  // being simply absent (the no-lessonId default).
  practice: boolean
  // The lesson's target (teaching) language code, e.g. "zh". Gates language-
  // specific note tools (pinyin readings, pronunciation voice). Absent outside a
  // session or when the tutor has no teaching language set.
  targetLanguageCode?: string
  // False while a real lesson's classroom is still deciding whether to put the
  // notes saved on the lesson back into an empty editor (#1034). The editor is
  // read-only and nothing is autosaved until it is true; it is always true in
  // the practice classroom, with collaboration off and outside a session.
  notesReady: boolean
}

const LiveDocContext = createContext<LiveDocValue | null>(null)

export function useLiveDoc(): LiveDocValue {
  const context = useContext(LiveDocContext)
  if (!context) {
    throw new Error("useLiveDoc must be used within a <LiveDocProvider>")
  }
  return context
}

// Graceful accessor for the classroom collaboration surfaces (whiteboard, notes,
// annotation). Inside a session it returns the shared, synced doc; rendered
// outside a <LiveDocProvider> (e.g. the static "How it works" marketing demo) it
// falls back to a throwaway local-only doc so those components still function in
// isolation. The fallback is created lazily and only when there's no provider.
export function useClassroomDoc(): LiveDocValue {
  const context = useContext(LiveDocContext)
  const fallbackRef = useRef<LiveDocValue | null>(null)
  if (!context && !fallbackRef.current) {
    const doc = new Y.Doc()
    // Awareness's constructor starts a cleanup setInterval, and effects (where we
    // destroy it) don't run during server-side rendering — so on the server (the
    // marketing demo SSRs this component) skip it to avoid leaking a timer per
    // render. It's only used inside effects/callbacks, which the server never runs.
    const awareness =
      typeof window === "undefined" ? (null as unknown as Awareness) : new Awareness(doc)
    fallbackRef.current = {
      doc,
      awareness,
      enabled: false,
      synced: false,
      selfColor: CURSOR_COLORS[0],
      practice: false,
      notesReady: true,
    }
  }
  useEffect(() => {
    return () => {
      if (fallbackRef.current) {
        fallbackRef.current.awareness?.destroy()
        fallbackRef.current.doc.destroy()
      }
    }
  }, [])
  return context ?? (fallbackRef.current as LiveDocValue)
}

interface LiveDocProviderProps {
  userName: string
  // Whether the real-time collab layer is on for THIS lesson. Resolved server-
  // side on the lesson (JoinLesson) so BOTH participants get the same value — a
  // per-viewer flag read would split them on a partial rollout.
  collabEnabled: boolean
  lessonId?: string
  targetLanguageCode?: string
  practice?: boolean
  children: ReactNode
}

// The notes saved on the lesson. Read apart from JoinLesson on purpose: if it
// fails, the notes are simply not restored, and nobody is kept out of the class.
const SAVED_NOTES_QUERY = graphql(`
  query ClassroomSavedNotes($id: ID!) {
    lesson(id: $id) {
      id
      notes
    }
  }
`)

// How long the editor waits for the saved notes and for a peer's copy before it
// opens anyway. A peer's copy normally arrives in well under a second.
export const NOTES_DECISION_DEADLINE_MS = 10_000
// How long the call object may be missing before it counts as unavailable.
export const NO_CALL_GRACE_MS = 1_500

// Inbound app-messages buffered before the transport attaches are capped so a
// transport that never attaches can't grow the buffer without bound.
const MAX_BUFFERED_INBOUND = 256

// Creates one Y.Doc + Awareness for the lifetime of a classroom session and,
// when collab is enabled for the lesson, syncs them across the two participants
// over Daily's data channel. Must render inside a <DailyProvider>.
export function LiveDocProvider({ userName, collabEnabled, lessonId, targetLanguageCode, practice = false, children }: LiveDocProviderProps) {
  const daily = useDaily()
  const meetingState = useMeetingState()
  const [synced, setSynced] = useState(false)

  // One doc + awareness per mount. A ref (not useMemo) guarantees a single
  // instance even under React StrictMode's double-invoked render.
  const docRef = useRef<Y.Doc>()
  if (!docRef.current) docRef.current = new Y.Doc()
  const doc = docRef.current

  const awarenessRef = useRef<Awareness>()
  if (!awarenessRef.current) awarenessRef.current = new Awareness(doc)
  const awareness = awarenessRef.current

  const selfColor = useMemo(() => colorForClient(doc.clientID), [doc])
  const transportRef = useRef<DailyTransport | null>(null)
  // App-messages that arrived before the transport attached (the tick between
  // joining and the attach effect, or meeting-state churn). Drained on attach so
  // a late joiner doesn't miss the peer's initial sync.
  const pendingInboundRef = useRef<Array<{ data: unknown; fromId: string }>>([])

  // Advertise our identity for live cursors.
  useEffect(() => {
    awareness.setLocalStateField("user", { name: userName, color: selfColor })
  }, [awareness, userName, selfColor])

  // Attach the Daily transport + sync provider once the flag is on AND we've
  // actually joined the call. The provider's constructor synchronously broadcasts
  // its first sync step, and Daily's sendAppMessage throws until the call reaches
  // "joined-meeting" — gating on meetingState is what keeps that throw out of the
  // React error boundary (see dailyAppMessageSend). Tearing down on any of these
  // changing cleanly drops our cursor.
  useEffect(() => {
    if (!collabEnabled || !daily || meetingState !== "joined-meeting") return

    const transport = new DailyTransport(dailyAppMessageSend(daily))
    transportRef.current = transport
    const provider = new LiveYjsProvider(doc, awareness, transport)
    provider.onSynced(() => setSynced(true))
    // (Re)advertise identity here too: a transport rebuild (Daily reconnect /
    // flag flip) runs provider.destroy(), which wipes our local awareness state,
    // and the separate identity effect won't re-fire — so without this a
    // reconnected participant would show up to their peer as a nameless cursor.
    awareness.setLocalStateField("user", { name: userName, color: selfColor })

    // Drain app-messages that arrived before this attach — the provider is now
    // subscribed, so they reach the CRDT instead of being dropped.
    if (pendingInboundRef.current.length) {
      const buffered = pendingInboundRef.current
      pendingInboundRef.current = []
      for (const { data, fromId } of buffered) transport.handleIncoming(data, fromId)
    }

    return () => {
      provider.destroy()
      transportRef.current = null
      setSynced(false)
    }
  }, [collabEnabled, daily, meetingState, doc, awareness, userName, selfColor])

  // Pump inbound Daily data messages into the transport. fromId namespaces each
  // sender's chunk stream; non-Yjs app-messages are ignored downstream. If the
  // transport isn't attached yet (just-joined tick / meeting-state churn), buffer
  // the message so it isn't silently dropped — the attach effect drains it.
  const handleAppMessage = useCallback((event: { data?: unknown; fromId?: string }) => {
    const fromId = event?.fromId ?? "unknown"
    const transport = transportRef.current
    if (transport) {
      transport.handleIncoming(event?.data, fromId)
    } else if (pendingInboundRef.current.length < MAX_BUFFERED_INBOUND) {
      pendingInboundRef.current.push({ data: event?.data, fromId })
    }
  }, [])
  useDailyEvent("app-message", handleAppMessage)

  // When a peer leaves (gracefully or not), prune their Yjs awareness now rather
  // than waiting ~30s for y-protocols' outdatedTimeout — otherwise their cursor
  // and any in-progress draw-preview freeze on our screen. Their own clean remove
  // may never arrive (their transport is already gone). 2-person room → drop
  // every non-local awareness state.
  const handleParticipantLeft = useCallback(() => {
    const remoteIds = Array.from(awareness.getStates().keys()).filter((id) => id !== doc.clientID)
    if (remoteIds.length) removeAwarenessStates(awareness, remoteIds, "participant-left")
  }, [awareness, doc])
  useDailyEvent("participant-left", handleParticipantLeft)

  // #1034: a rejoining participant opens a brand-new, empty doc. If nobody else
  // holds the notes, put the ones saved on the lesson back; if a peer is there,
  // wait for their copy instead. Decided ONCE per page load: an in-page
  // reconnect (transport rebuilt) never restores again, so notes a person
  // deliberately emptied do not come back. (This departs from "persistence
  // stays additive" of #549/#550 on purpose: the saved text is written into the
  // shared doc.)
  const needsRestore = collabEnabled && !!lessonId && !practice
  const [notesReady, setNotesReady] = useState(!needsRestore)
  // undefined: not answered yet. null: the read failed (restore skipped).
  const [savedNotes, setSavedNotes] = useState<string | null | undefined>(undefined)
  const decidedRef = useRef(!needsRestore)

  useEffect(() => {
    if (!needsRestore || !lessonId) return
    let cancelled = false
    graphqlClient
      .request(SAVED_NOTES_QUERY, { id: lessonId })
      .then((data) => !cancelled && setSavedNotes(data.lesson?.notes ?? ""))
      .catch(() => !cancelled && setSavedNotes(null))
    return () => {
      cancelled = true
    }
  }, [needsRestore, lessonId])

  const decide = useCallback(
    (restore: boolean) => {
      if (decidedRef.current) return
      decidedRef.current = true
      if (restore && lessonId && typeof savedNotes === "string") restoreSavedNotes(doc, lessonId, savedNotes)
      setNotesReady(true)
    },
    [doc, lessonId, savedNotes],
  )

  // Open anyway after the deadline: no restore, but nobody is locked out.
  useEffect(() => {
    if (decidedRef.current) return
    const timer = setTimeout(() => decide(false), NOTES_DECISION_DEADLINE_MS)
    return () => clearTimeout(timer)
  }, [decide])

  // No call object after a moment (WebRTC unavailable): nothing can ever reach
  // this doc, so there is no peer's copy to wait for.
  const [noCall, setNoCall] = useState(false)
  useEffect(() => {
    if (daily) {
      setNoCall(false)
      return
    }
    const timer = setTimeout(() => setNoCall(true), NO_CALL_GRACE_MS)
    return () => clearTimeout(timer)
  }, [daily])

  useEffect(() => {
    if (decidedRef.current || savedNotes === undefined) return
    // A call that failed to start ("error") is as alone as it gets too.
    if (meetingState !== "joined-meeting" && meetingState !== "error" && !noCall) return
    const peers = Object.keys(daily?.participants?.() ?? {}).filter((id) => id !== "local")
    // With a peer in the room, wait for their state: they may hold the notes.
    if (peers.length > 0 && !synced) return
    decide(true)
  }, [savedNotes, meetingState, noCall, synced, daily, decide])

  // Destroy the doc/awareness only when the session fully unmounts.
  useEffect(() => {
    return () => {
      awareness.destroy()
      doc.destroy()
    }
  }, [doc, awareness])

  const value = useMemo<LiveDocValue>(
    () => ({ doc, awareness, enabled: collabEnabled, synced, selfColor, lessonId, targetLanguageCode, practice, notesReady }),
    [doc, awareness, collabEnabled, synced, selfColor, lessonId, targetLanguageCode, practice, notesReady],
  )

  return <LiveDocContext.Provider value={value}>{children}</LiveDocContext.Provider>
}
